import {isUndefined} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import noResultsImageSrc from '../../../../../../../src/assets/images/noResults.svg';
import {NamespaceAvatarContainer} from '../../../../../../../src/components/common/avatars/namespaceAvatarContainer';
import {EditCardGroupPopoverContainer} from '../../../../../../../src/components/contacts/common/editCardGroupPopoverContainer';
import {contactsLearnMoreUrl} from '../../../../../../../src/helpers/contactsManagerHelpers';
import {isAutoGlobalCard} from '../../../../../../../src/helpers/models/cardHelpers';
import {
  computeIsGlobalNamespace,
  isTeammateNamespace,
} from '../../../../../../../src/helpers/models/namespaceHelpers';
import {CardGroupModel} from '../../../../../../../src/models/api/cardGroupModel';
import {CardModel} from '../../../../../../../src/models/api/cardModel';
import {TeamModel} from '../../../../../../../src/models/api/teamModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../../src/services/tracking';
import {AddCardGroupToCardsOptions} from '../../../../../../../src/store/data/cardGroups/cardGroupsActions';
import {Plural, Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {
  AsyncHandlerOf,
  Handler,
  HandlerOf,
  RendererOf,
} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {PopoverCoordinator} from '../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../shared/ui/src/components/tooltips/tooltipCoordinator';
import {AddContactButton} from '../common/buttons/addContactButton';
import {useContactsManagerContext} from '../contactsManagerContext';
import {ContactsManagerHeader} from '../contactsManagerHeader';
import {DeleteCardGroupConfirmationDialog} from '../dialogs/deleteCardGroupConfirmationDialog';
import {OtherCardNamespaceConfirmationDialogContainer} from '../dialogs/otherCardNamespaceConfirmationDialogContainer';
import {
  buildContactsManagerEditCardGroupsEvent,
  ContactsManagerEditCardGroupsTrackingActionsEnum,
} from '../helpers/trackingHelpers';
import {ContactsTableMultiSelectActions} from '../tables/common/contacts/contactsTableMultiSelectActions';
import {ContactsManagerTableEmptyState} from '../tables/common/contactsManagerEmptyState';
import {ListContactsTableContainer} from '../tables/listContacts/listContactsTableContainer';

/*
 * Props.
 */

export interface CardGroupContactsPageProps {
  cardGroup?: CardGroupModel;
  selectedCardIds: ReadonlyArray<number>;
  team?: TeamModel;
  navigateBackToLists: Handler;
  addCardGroupToCardsAsync: HandlerOf<AddCardGroupToCardsOptions>;
  onDeleteCardGroupsAsync: AsyncHandlerOf<ReadonlyArray<number>>;
  contactsManagerTableAddCardIdRefs: HandlerOf<ReadonlyArray<number>>;
}

/*
 * Styles.
 */

const StyledImg = styled.img`
  width: 92px;
`;

const StyledDescription = styled.div`
  width: 247px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
  padding: 0 3px 0 3px;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  margin-right: 8px;
`;

/*
 * Component.
 */

export const CardGroupContactsPage: FC<CardGroupContactsPageProps> = ({
  cardGroup,
  selectedCardIds,
  team,
  addCardGroupToCardsAsync,
  navigateBackToLists,
  onDeleteCardGroupsAsync,
  contactsManagerTableAddCardIdRefs,
}) => {
  const [isShowingConfirmNamespaceDialog, setIsShowingConfirmNamespaceDialog] = useState(false);
  const [isShowingDeleteCardGroupConfirmationDialog, setIsShowingDeleteCardGroupConfirmationDialog] =
    useState(false);
  const [selectedCard, setSelectedCard] = useState<CardModel | undefined>(undefined);
  const {panelId, onTrackHandler} = useContactsManagerContext();

  const openConfirmNamespaceDialog = (card: CardModel) => {
    setSelectedCard(card);
    setIsShowingConfirmNamespaceDialog(true);
  };

  const closeConfirmNamespaceDialog = () => {
    setIsShowingConfirmNamespaceDialog(false);
    setSelectedCard(undefined);
  };

  const maybeRenderNamespaceConfirmationDialog = () => {
    if (!isShowingConfirmNamespaceDialog || !cardGroup || !selectedCard) {
      return null;
    }

    return (
      <OtherCardNamespaceConfirmationDialogContainer
        cardGroups={[cardGroup]}
        cards={[selectedCard]}
        onClose={closeConfirmNamespaceDialog}
        onSuccess={contactsManagerTableAddCardIdRefs}
        panelId={panelId}
      />
    );
  };

  const maybeRenderDeleteCardGroupConfirmationDialog = () => {
    if (!isShowingDeleteCardGroupConfirmationDialog || !cardGroup) {
      return null;
    }

    return (
      <DeleteCardGroupConfirmationDialog
        numberToDelete={1}
        onClose={() => setIsShowingDeleteCardGroupConfirmationDialog(false)}
        onDelete={async () => {
          await onDeleteCardGroupsAsync([cardGroup?.id]);
          if (cardGroup) {
            trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CARD_GROUP_DELETE, {
              namespace: cardGroup.namespace,
              num_deleted: 1,
            });
          }
          navigateBackToLists();
        }}
      />
    );
  };

  const addCardToGroupAsync = async (cardId: number) => {
    if (!cardGroup) {
      return;
    }
    await addCardGroupToCardsAsync({cardGroupId: cardGroup.id, cardIds: [cardId]});
    contactsManagerTableAddCardIdRefs([cardId]);
    onTrackHandler(
      buildContactsManagerEditCardGroupsEvent(ContactsManagerEditCardGroupsTrackingActionsEnum.ADD, 1),
    );
  };

  const renderEditCardGroupButton = () => {
    if (!cardGroup) {
      return null;
    }
    return (
      <StyledPopoverCoordinator
        renderButton={renderEditCardGroupPopoverButton}
        renderModal={(popoverProps) => (
          <EditCardGroupPopoverContainer
            cardGroup={cardGroup}
            popoverProps={popoverProps}
            onClose={popoverProps.onRequestClose}
            onDelete={() => setIsShowingDeleteCardGroupConfirmationDialog(true)}
          />
        )}
      />
    );
  };

  const renderAddContactButton = (isPrimary?: boolean) => {
    if (!cardGroup) {
      return null;
    }
    return (
      <AddContactButton
        computeIsContactDisabled={(card) => card.groupIds.includes(cardGroup.id)}
        variant={isPrimary ? ButtonVariantsEnum.PRIMARY : ButtonVariantsEnum.SECONDARY}
        placement={isPrimary ? 'top-end' : 'top-start'}
        filterSearchResults={filterCard}
        onConfirm={async (card, popoverProps) => {
          if (isAutoGlobalCard(card)) {
            openConfirmNamespaceDialog(card);
          } else {
            await addCardToGroupAsync(card.id);
          }

          popoverProps?.onRequestClose();
        }}
      />
    );
  };

  return (
    <>
      <ContactsManagerHeader
        title={cardGroup?.name}
        contextDetails={maybeRenderContextDetails(cardGroup?.numberOfCards)}
        interactionId="contacts-manager-list-contacts-header"
        topLevelBreadcrumb={{
          label: <Trans id="contacts-manager-list-contacts-breadcrumb">Lists</Trans>,
          onBreadcrumbClick: navigateBackToLists,
        }}
        avatar={maybeRenderNamespaceAvatar(cardGroup?.namespace, team)}
        actions={
          <>
            {renderEditCardGroupButton()}
            {renderAddContactButton(true)}
          </>
        }
        isLoading={!cardGroup}
      />
      {cardGroup && (
        <ListContactsTableContainer
          panelId={panelId}
          multiSelectHeaderActions={
            <ContactsTableMultiSelectActions
              panelId={panelId}
              cardIds={selectedCardIds}
              cardGroupId={cardGroup.id}
            />
          }
          renderEmpty={() => renderEmptyState(renderAddContactButton)}
          cardGroup={cardGroup}
        />
      )}
      {maybeRenderNamespaceConfirmationDialog()}
      {maybeRenderDeleteCardGroupConfirmationDialog()}
    </>
  );
};

/*
 * Helpers.
 */

function renderEditCardGroupPopoverButton() {
  return (
    <Button variant={ButtonVariantsEnum.SECONDARY}>
      <StyledButtonContentDiv>
        <Trans id="contacts-manager-list-edit">Edit list</Trans>
      </StyledButtonContentDiv>
    </Button>
  );
}

function filterCard(card: CardModel, cardGroup?: CardGroupModel) {
  return card.namespace === cardGroup?.namespace || computeIsGlobalNamespace(card.namespace);
}

function renderEmptyState(renderAddContactButton: RendererOf<boolean>) {
  return (
    <ContactsManagerTableEmptyState
      title={<Trans id="list-contacts-empty-state-title">No contacts</Trans>}
      image={<StyledImg alt="list-contacts-empty-state-image" src={noResultsImageSrc} />}
      description={
        <StyledDescription>
          <Trans id="list-contacts-empty-state-description">
            Lists make it easy to message multiple contacts at once.
          </Trans>
        </StyledDescription>
      }
      learnMoreUrl={contactsLearnMoreUrl}
    >
      {renderAddContactButton(false)}
    </ContactsManagerTableEmptyState>
  );
}

function maybeRenderNamespaceAvatar(namespace?: string, team?: TeamModel) {
  if (!namespace) {
    return null;
  }

  return (
    <TooltipCoordinator render={() => renderTooltipContent(namespace, team)} placement="top">
      <NamespaceAvatarContainer namespace={namespace} />
    </TooltipCoordinator>
  );
}

function renderTooltipContent(namespace: string, team?: TeamModel) {
  if (isTeammateNamespace(namespace)) {
    return <Trans id="list-contacts-access-tooltip-private">Only you can access this list</Trans>;
  }
  if (team) {
    return (
      <Trans id="list-contacts-access-tooltip-team">Only the {team.name} team can access this list</Trans>
    );
  }
  if (computeIsGlobalNamespace(namespace)) {
    return <Trans id="list-contacts-access-tooltip-company">The entire company can access this list</Trans>;
  }
  return null;
}

function maybeRenderContextDetails(totalResults: number | undefined) {
  if (isUndefined(totalResults)) {
    return null;
  }
  return <Plural id="list-contacts-count" value={totalResults} one="(# contact)" other="(# contacts)" />;
}
